import React from "react";
import { langs } from "@/i18n";
import { Contract, getAddress, BaseContractMethod, Wallet } from "ethers";
import { NET_ID } from "@/config";
import { cdnUrl } from "./cdn";
import BigNumber from "bignumber.js";
import Decimal from "decimal.js";
// utils/download.ts
// import * as XLSX from "xlsx";

/**
 * 检查是否是有效的以太坊地址
 * @param address - 待验证的钱包地址
 * @returns 返回 true 如果地址有效，否则返回 false
 */
export function isValidEVMAddress(address: string): boolean {
  // // 检查地址是否是以 '0x' 开头，并且长度为 42 (0x + 40 十六进制字符)
  // if (!/^0x[a-fA-F0-9]{40}$/.test(address)) {
  //   return false;
  // }

  // // 校验 EIP-55 校验和 (混合大小写的地址)
  // // try {
  // //   const checksumAddress = getAddress(address);
  // //   return checksumAddress === address;
  // // } catch (e) {
  // //   return false;
  // // }
  // return true;
  return /^0x[0-9a-fA-F]{64}$/.test(address);
}

export function isValidContractAddress(address: string): boolean {
  // 检查地址是否是以 '0x' 开头，并且长度为 42 (0x + 40 十六进制字符)
  if (!/^0x[a-fA-F0-9]{40}$/.test(address)) {
    return false;
  }

  // 不强制检查 EIP-55 校验和，支持合约地址
  return true;
}

export function getChainsResource(): any {
  console.log("NET_ID", NET_ID)
  if (NET_ID === 56 || NET_ID === 97) {
    return {
      imgPathPrefix: "/img/bsc/",
      urlSuffix: "bsc",
      webTittle: "SlerfTools-BSC",
    };
  }
  if (NET_ID === 8453 || NET_ID === 84532) {
    return {
      imgPathPrefix: "/img/base/",
      urlSuffix: "eth",
      webTittle: "SlerfTools-BASE",
    };
  }
  return {
    imgPathPrefix: "/img/eth/",
    urlSuffix: "sui",
    webTittle: "SlerfTools-sui",
  };
}

export async function processBatch<T>(
  tasks: (() => Promise<T>)[],
  delay: number,
  batchSize: number
): Promise<T[]> {
  const results: T[] = [];

  for (let i = 0; i < tasks.length; i += batchSize) {
    const batch = tasks.slice(i, i + batchSize);

    const batchResults = await Promise.allSettled(
      batch.map((task, index) => {
        const taskDelay = index * delay;

        return new Promise<T>((resolve) => {
          setTimeout(async () => {
            const result = await task();
            resolve(result);
          }, taskDelay);
        });
      })
    );

    results.push(
      ...batchResults
        .filter((result) => result.status === "fulfilled")
        .map((result) => (result as PromiseFulfilledResult<T>).value)
    );
  }

  return results;
}

export const numberWithCommas = (x: number, digits: number = 3): string => {
  return parseFloat(x.toString()).toLocaleString(undefined, {
    maximumFractionDigits: digits,
  });
};

export const ellipsisAddress = (address: string | undefined): string => {
  return address?.slice(0, 9) + "..." + address?.slice(-9);
};

export const formatNumber = (number: number): string => {
  let suffix = "";
  let formattedNumber = number;

  if (number >= 1e6) {
    suffix = "M";
    formattedNumber = number / 1e6;
  } else if (number >= 1e3) {
    suffix = "k";
    formattedNumber = number / 1e3;
  }
  return formattedNumber && formattedNumber > 0
    ? `${parseFloat(formattedNumber.toString()).toFixed(2)}${suffix}`
    : "0";
};

export function arrayObjectToflat<T>(
  list: T[],
  key: keyof T,
  hasKey: boolean = false
): T[] {
  const res: T[] = [];
  list.forEach((el) => {
    if (Array.isArray(el[key])) {
      // 检查 el[key] 是否是数组
      res.push(...arrayObjectToflat(el[key] as unknown as T[], key)); // 类型断言
    }
    if (!hasKey) {
      delete el[key];
    }
    res.push(el);
  });
  return res;
}

export function getTextFromReactNode(
  node: React.ReactNode
): string | undefined {
  if (typeof node === "string" || typeof node === "number") {
    return `${node}`;
  }
  if (Array.isArray(node)) {
    return node.map(getTextFromReactNode).join("");
  }
  if (React.isValidElement(node)) {
    const childrenText = React.Children.map(
      node.props.children,
      getTextFromReactNode
    )
      ?.filter(Boolean)
      ?.join("");
    return childrenText;
  }
  return "";
}

export function insertNewNode(str: string): string {
  if (str.indexOf("undefined") > -1) {
    return str.replace(
      "undefined",
      ` <img src="${cdnUrl("/img/slerf/new.svg")}" alt="" />`
    );
  }
  return str;
}

export function isObjectEqual(a: any, b: any): boolean {
  if (a === b) return true;
  if (!a || !b) return false;

  const aProps = Object.getOwnPropertyNames(a);
  const bProps = Object.getOwnPropertyNames(b);

  if (aProps.length !== bProps.length) return false;

  for (const prop in a) {
    if (b.hasOwnProperty(prop)) {
      if (typeof a[prop] === "object") {
        if (!isObjectEqual(a[prop], b[prop])) return false;
      } else if (a[prop] !== b[prop]) {
        return false;
      }
    } else {
      return false;
    }
  }
  return true;
}

export function replaceLangPath(url: string): string {
  if (url === "/") {
    url = `/${langs[0]}`;
  }
  const paths = langs
    .map((lang) => {
      // 使用英文括号
      const hasLang = url.indexOf(lang) > -1;
      return hasLang ? url.replace(lang, ":lang") : null;
    })
    .filter((it) => it !== null);
  return paths.length > 0 ? paths[0] : "";
}

export function dealScienceNubmer(numStr: string): string {
  const temp = numStr.toUpperCase().split("E+");
  const tempReduce = numStr.toUpperCase().split("E-");

  if (!temp[1] && !tempReduce[1]) {
    return numStr;
  } else {
    if (temp[1]) {
      const arr = temp[0].split(".");
      let tempNumStr = arr[0];
      let lengths = 0;
      if (arr[1]) {
        tempNumStr = `${arr[0]}${arr[1]}`;
        lengths = arr[1].length;
      }
      for (let i = 0; i < parseInt(temp[1]) - lengths; i++) {
        tempNumStr += "0";
      }
      return tempNumStr;
    } else {
      if (parseInt(tempReduce[1]) > 8) {
        return "0";
      }
      let tempNumStr = "0.";
      for (let i = 0; i < parseInt(tempReduce[1]) - 1; i++) {
        tempNumStr += "0";
      }
      return tempNumStr + tempReduce[0];
    }
  }
}

export const number_reg = (decimals: number): RegExp => {
  return RegExp(`^(0|[1-9]\\d*)((\\.)?|(\\.\\d{1,${decimals}}))?$`);
};

// export function isValidPrivateKeyBase58(privateKeyBase58: any = "") {
//   return false;
// }

// // 检查二进制私钥
// export function isValidPrivateKeyBytes(privateKeyBytes: any = "") {
//   return false;
// }

export function roundToGwei(amount: bigint): bigint {
  const GWEI_UNIT = 10n ** 9n; // 1 Gwei = 10^9 wei
  return (amount / GWEI_UNIT) * GWEI_UNIT; // 向下取整到最接近的 Gwei 倍数
}

//获取两数之间的随机数
export function getRandomFloat(min: number, max: number) {
  return Math.random() * (max - min) + min;
}

/**
 * 将科学计数法数字转换为完整字符串表示
 * @param num 需要转换的数字
 * @returns 转换后的数字字符串
 */
export function formatScientific(num: any): string {
  // 处理非数字、无穷大等特殊情况
  if (!isFinite(num)) {
    return num.toString();
  }

  const str = num.toString();

  // 如果不是科学计数法表示，直接返回
  if (!str.includes("e") && !str.includes("E")) {
    return str;
  }

  // 分解科学计数法的系数和指数
  const [coefficient, exponentStr] = str.toLowerCase().split("e");
  const exponent = parseInt(exponentStr, 10);

  // 处理系数部分
  const [integerPart, fractionalPart = ""] = coefficient.split(".");
  const fullDigits = integerPart + fractionalPart;
  const digitCount = fractionalPart.length;

  // 处理正指数 (1.23e+5 → 123000)
  if (exponent > 0) {
    const zerosNeeded = exponent - digitCount;
    if (zerosNeeded > 0) {
      return fullDigits + "0".repeat(zerosNeeded);
    } else {
      return (
        fullDigits.slice(0, integerPart.length + exponent) +
        (exponent < fullDigits.length ? "." : "") +
        fullDigits.slice(integerPart.length + exponent)
      );
    }
  }
  // 处理负指数 (1.23e-5 → 0.0000123)
  else {
    const leadingZeros = Math.abs(exponent) - 1;
    return (
      (num < 0 ? "-" : "") + // 保留负号
      "0." +
      "0".repeat(leadingZeros) +
      fullDigits.replace("-", "") // 去除系数中的负号
    );
  }
}
// 移除小数尾部0
export function removeTrailingZeros(num: number | string) {
  return num?.toString().replace(/(\.\d*?[1-9])0+$|\.0+$/, "$1");
}
// 数字处理 小于digitCount精度位，返回<=min
export function numberFormat(num: string, digitCount = 4, precision = 18) {
  // console.log('num',num,digitCount,precision)
  const min = new BigNumber(1).dividedBy(new BigNumber(10).pow(digitCount));
  if (new BigNumber(num).isEqualTo(0)) {
    return "0";
  } else if (
    new BigNumber(
      new BigNumber(num).toFixed(precision, 1)
    ).isLessThanOrEqualTo(min)
    && new BigNumber(num).isLessThanOrEqualTo(min)
  ) {
    // console.log('min ?? ',num, min.toString())
    return `≤ ${min.toString()}`;
  }
  return removeTrailingZeros(
    new BigNumber(num).toFormat(precision, BigNumber.ROUND_DOWN)
  );
}

//判断是不是正确的私钥
export function isValidPrivateKey(privateKey: string): boolean {
  console.log("ppk", privateKey);
  try {
    const normalizedKey = privateKey.startsWith("0x")
      ? privateKey
      : `0x${privateKey}`;
    const wallet = new Wallet(normalizedKey);
    return true;
  } catch (error) {
    return false;
  }
}

export function countTrailingZeros(num: any) {
  const str = num.toString();
  console.log("str", str);
  let count = 0;
  for (let i = str.length - 1; i >= 0; i--) {
    if (str[i] === "0") {
      count++;
    } else {
      break;
    }
  }
  return count;
}
export const formatBalance = (balance: any) => {
  const num = Number(balance);
  if (isNaN(num)) return "0";
  return num.toString().includes(".")
    ? num.toFixed(6).replace(/\.?0+$/, "") // 去除尾部 0 和可能多余的点
    : num.toString();
};

// 通用超时检测工具函数 - 接受秒为单位的时间参数
export const createTimeoutPromise = (
  timeoutSeconds: number,
  errorMessage: string
) => {
  return new Promise((_, reject) => {
    setTimeout(() => {
      reject(new Error(errorMessage));
    }, timeoutSeconds * 1000); // 将秒转换为毫秒
  });
};

//decimal乘法
export const decimalTimes = (a: string, b: string) => {
  const first = new Decimal(a);
  const second = new Decimal(b);
  return first.mul(second).toString();
};
//decimal加法
export const decimalAdd = (a: string, b: string) => {
  const first = new Decimal(a);
  const second = new Decimal(b);
  return first.plus(second).toString();
};
// 限制小数位数
export function limitPrecision(value: any, maxDigits = 4) {
  // 转换为字符串处理
  const str = String(value);

  // 检查是否为科学计数法 (如 1.23e-5)
  if (str.includes("e") || str.includes("E")) {
    // 科学计数法需特殊处理
    return removeTrailingZeros(parseFloat(value).toFixed(maxDigits));
  }

  // 分离整数和小数部分
  const [integerPart, decimalPart = ""] = str.split(".");

  // 如果没有小数或小数位数不足，直接返回原值
  if (decimalPart.length <= maxDigits) {
    return removeTrailingZeros(value);
  }

  // 截取指定位数的小数
  const truncatedDecimal = decimalPart.slice(0, maxDigits);
  return removeTrailingZeros(`${integerPart}.${truncatedDecimal}`);
}

/**
 * 生成 [min, max] 范围内的随机数，支持截断小数位数
 * @param {number} min - 最小值
 * @param {number} max - 最大值
 * @param {number} [n] - 可选，要保留的小数位数（不传则保留所有小数）
 * @returns {number} 生成的随机数
 */
export function randomInRange(min: number, max: number, n?: number) {
  // 生成 [min, max] 范围内的随机数
  const random = Math.random() * (max - min) + min;

  // 如果传入了 n 参数，则截断小数位数
  if (typeof n !== "undefined") {
    const factor = Math.pow(10, n);
    return Math.floor(random * factor) / factor;
  }

  return random;
}



export interface TieredFeeResult {
  total: string;   // 总费用（字符串）
  list: {
    count: number;    // 当前区间地址数量
    fee: string;      // 单个地址费用（字符串）
    total: string;    // 当前区间总费用（字符串）
  }[];
}
/**
 * 计算阶梯收费（非累加模式）
 * @param addressCount 地址总数
 * @param tiers 阶梯配置数组 [{max, fee}]
 * @param minFee 最低收费（默认 0.005）
 * @returns 当前阶梯费用明细 + 总费用（字符串格式）
 */
export function calculateTieredFee(
  addressCount: number,
  tiers: {
    max: number;
    fee: string | number;
  }[],
  minFee: string | number = 0.005
): TieredFeeResult {
  if (addressCount <= 0) {
    return { total: new BigNumber(minFee).toString(), list: [] };
  }

  let selectedTier = tiers.find(tier => addressCount <= tier.max);

  if (!selectedTier) {
    // 如果地址数超过最大阶梯，使用最后一个阶梯
    selectedTier = tiers[tiers.length - 1];
  }

  const fee = new BigNumber(selectedTier.fee);
  const total = fee.multipliedBy(addressCount);

  const finalTotal = total.lt(minFee) ? new BigNumber(minFee) : total;

  return {
    total: finalTotal.toString(),
    list: [
      {
        count: addressCount,
        fee: fee.toString(),
        total: finalTotal.toString(),
      }
    ]
  };
}
